/**
 * Handler centralizado para erros da API Tools
 *
 * Mapeia códigos específicos da API, detecta contexto por URL e converte
 * erros da API em AppError padronizado com mensagens do ErrorCatalog.
 */

import { APIError } from "./api";
import { AppError, ErrorCode, fromApi } from "./errors";
import { sendToTerminal } from "./terminalLogger";
import { LogCategory } from "../config/logging";
import { ErrorCatalog } from "../components/feedback/ErrorCatalog";

/**
 * Mapeia códigos específicos retornados pela API para ErrorCode
 */
const API_CODE_TO_ERROR_CODE: Record<string, ErrorCode> = {
	// Launcher/Session
	launcher_session_required: ErrorCode.LAUNCHER_NOT_READY,
	LAUNCHER_NOT_READY: ErrorCode.LAUNCHER_NOT_READY,

	// Tools
	INVALID_SLUG: ErrorCode.VALIDATION_ERROR,
	TOOL_NOT_FOUND: ErrorCode.TOOL_NOT_FOUND,
	COMMAND_NOT_FOUND: ErrorCode.COMMAND_NOT_FOUND,
	INVALID_ID: ErrorCode.VALIDATION_ERROR,

	// Budget/Orçamento
	CLIENTS_NOT_FOUND: ErrorCode.CLIENTS_NOT_FOUND,
	PRODUCT_NOT_FOUND: ErrorCode.PRODUCT_NOT_FOUND,
	CATEGORIA_ERROR: ErrorCode.CATEGORIA_ERROR,

	// Rules/Qualities
	RULES_NOT_FOUND: ErrorCode.RULES_NOT_FOUND,
	QUALITIES_NOT_FOUND: ErrorCode.QUALITIES_NOT_FOUND,
	REGRAS_ERROR: ErrorCode.REGRAS_ERROR,

	// PlotCalc
	CONFIG_NOT_FOUND: ErrorCode.CONFIG_NOT_FOUND,
	NO_SERIAL_AVAILABLE: ErrorCode.NO_SERIAL_AVAILABLE,
	TENANT_REQUIRED: ErrorCode.TENANT_REQUIRED,

	// Discount
	NO_DISCOUNT_FOUND: ErrorCode.NO_DISCOUNT_FOUND,

	// Gateway errors
	BAD_GATEWAY: ErrorCode.BAD_GATEWAY,
	GATEWAY_TIMEOUT: ErrorCode.GATEWAY_TIMEOUT,
	CONNECTION_ERROR: ErrorCode.BAD_GATEWAY,
	TIMEOUT: ErrorCode.GATEWAY_TIMEOUT,
	// Autenticação Oneprint (401)
	USER_NOT_FOUND: ErrorCode.USER_NOT_FOUND,
	USER_ID_REQUIRED: ErrorCode.USER_ID_REQUIRED,
	// Validação Oneprint (400)
	MISSING_REQUIRED_FIELDS: ErrorCode.MISSING_REQUIRED_FIELDS,
	INVALID_IMAGES: ErrorCode.INVALID_IMAGES,
	INVALID_MD5: ErrorCode.INVALID_MD5,
	INVALID_PAYLOAD: ErrorCode.INVALID_PAYLOAD,
	INVALID_KEY: ErrorCode.INVALID_KEY,
	INVALID_PERM_ID: ErrorCode.INVALID_PERM_ID,
	INVALID_BODY: ErrorCode.INVALID_BODY,
	INVALID_QUERY: ErrorCode.INVALID_QUERY,
	MISSING_PARAMETERS: ErrorCode.MISSING_PARAMETERS,
	EMPTY_UPDATES: ErrorCode.EMPTY_UPDATES,
	// Conflito Oneprint (409)
	PEDIDO_JA_SALVO: ErrorCode.PEDIDO_JA_SALVO,
	// Tenant Oneprint (400)
	TENANT_INVALID: ErrorCode.TENANT_INVALID,
	// Autorização Oneprint (403)
	UNAUTHORIZED: ErrorCode.UNAUTHORIZED_403,
	// Não encontrado Oneprint (404)
	ITEM_NOT_FOUND: ErrorCode.ITEM_NOT_FOUND,
	ORCAMENTO_NOT_FOUND: ErrorCode.ORCAMENTO_NOT_FOUND,
	// Servidor Oneprint (500)
	INTERNAL_ERROR: ErrorCode.INTERNAL_ERROR,
	UNEXPECTED_ERROR: ErrorCode.UNEXPECTED_ERROR,
	UNSPECIFIED: ErrorCode.UNSPECIFIED,
	ER_BAD_FIELD_ERROR: ErrorCode.ER_BAD_FIELD_ERROR,
	// Erros Prisma
	P2002: ErrorCode.P2002,
	P2003: ErrorCode.P2003,
	P2021: ErrorCode.P2021,
};

/**
 * Tipo de contexto para detecção de erros específicos
 */
export type ToolsErrorContext =
	| "tool"
	| "command"
	| "escalonamento"
	| "plotcalc"
	| "rules"
	| "qualities"
	| "budget"
	| "discount"
	| "config";

/**
 * Detecta o contexto do erro baseado na URL do endpoint
 *
 * ✅ SIMPLIFICADO (Fase 4): Usa mensagem do backend quando disponível,
 * fallback para contexto genérico
 */
function detectContextFromUrl(
	url: string | undefined,
): ToolsErrorContext | null {
	if (!url) return null;

	const urlLower = url.toLowerCase();

	// ✅ Prioridade 1: Tools e Commands (mais comum)
	if (urlLower.includes("/tools/commands/")) return "command";
	if (urlLower.includes("/tools/")) return "tool";

	// ✅ Prioridade 2: APIs críticas (mantidas para compatibilidade)
	if (urlLower.includes("/plotcalc/")) return "plotcalc";
	if (urlLower.includes("/escalonamento/calcular/")) return "escalonamento";

	// ✅ Prioridade 3: Outras APIs (agrupadas - usar mensagem do backend)
	if (urlLower.includes("/cdr/rules/")) return "rules";
	if (urlLower.includes("/categoria/one1-outros/")) return "qualities";
	if (urlLower.includes("/getall-client/")) return "budget";
	if (urlLower.includes("/produto-categoria-item")) return "budget";
	if (urlLower.includes("/nivel-desconto/")) return "discount";
	if (urlLower.includes("/cdr/config/")) return "config";
	if (urlLower.includes("/regras-salvamento")) return "config";

	return null;
}

/**
 * Mapeia status codes para ErrorCode baseado no contexto
 */
function mapStatusCodeToErrorCode(
	status: number,
	context: ToolsErrorContext | null,
	url?: string,
): ErrorCode | null {
	// 404 - Not Found (contexto específico)
	if (status === 404) {
		switch (context) {
			case "tool":
				return ErrorCode.TOOL_NOT_FOUND;
			case "command":
				return ErrorCode.COMMAND_NOT_FOUND;
			case "escalonamento":
				return ErrorCode.PRODUCT_NOT_FOUND;
			case "qualities":
				return ErrorCode.QUALITIES_NOT_FOUND;
			case "rules":
				return ErrorCode.RULES_NOT_FOUND;
			case "plotcalc":
				return ErrorCode.CONFIG_NOT_FOUND;
			case "budget":
				// Pode ser clients ou product
				if (url?.includes("/getall-client/")) {
					return ErrorCode.CLIENTS_NOT_FOUND;
				}
				return ErrorCode.PRODUCT_NOT_FOUND;
			case "discount":
				return ErrorCode.NO_DISCOUNT_FOUND;
			default:
				return null; // Usar fallback genérico
		}
	}

	// 400 - Bad Request (contexto específico)
	if (status === 400) {
		if (context === "plotcalc") {
			// Verificar se é TENANT_REQUIRED
			return ErrorCode.TENANT_REQUIRED;
		}
		return ErrorCode.VALIDATION_ERROR;
	}

	// 424 - Failed Dependency (PlotCalc serial)
	if (status === 424 && context === "plotcalc") {
		return ErrorCode.NO_SERIAL_AVAILABLE;
	}

	// 502 - Bad Gateway
	if (status === 502) {
		return ErrorCode.BAD_GATEWAY;
	}

	// 504 - Gateway Timeout
	if (status === 504) {
		return ErrorCode.GATEWAY_TIMEOUT;
	}

	// 409 - Conflict (validação)
	if (status === 409) {
		return ErrorCode.VALIDATION_ERROR;
	}

	// 500 - Internal Server Error (contexto específico)
	if (status === 500) {
		if (context === "budget") {
			// Pode ser CATEGORIA_ERROR ou REGRAS_ERROR
			if (url?.includes("/produto-categoria-item")) {
				return ErrorCode.CATEGORIA_ERROR;
			}
			if (url?.includes("/regras-salvamento")) {
				return ErrorCode.REGRAS_ERROR;
			}
		}
		return ErrorCode.API_5XX;
	}

	return null;
}

/**
 * Extrai código de erro específico da resposta da API
 */
function extractApiErrorCode(error: APIError): string | null {
	// Tentar múltiplas formas de extrair o código
	const code =
		error.data?.code ||
		error.data?.error?.code ||
		error.data?.errorCode ||
		error.message?.match(/\[([A-Z_]+)\]/)?.[1];

	return code ? String(code).trim() : null;
}

/**
 * Handler centralizado para erros da API Tools
 *
 * ✅ SIMPLIFICADO (Fase 4): Estratégia de tratamento de erros
 *
 * Ordem de prioridade:
 * 1. Código específico da API (apiCode) - mais preciso
 * 2. Status code + contexto - mapeamento contextual
 * 3. Mensagem do backend (quando disponível) - PRIORIZADO
 * 4. Fallback genérico - sempre funciona
 *
 * @param error - APIError da requisição
 * @param context - Contexto opcional (se não fornecido, tenta detectar pela URL)
 * @returns AppError padronizado com mensagem do ErrorCatalog
 */
export function handleToolsApiError(
	error: APIError,
	context?: ToolsErrorContext,
): AppError {
	// Criar cópia do erro para evitar mutação
	const errorData = error.data ? { ...error.data } : {};

	// Detectar contexto se não fornecido
	const detectedContext =
		context || detectContextFromUrl(errorData.url || error.message);

	// ✅ NOVO: Detectar erro de launcher não pronto (booting)
	const errorMsg = error.message || "";
	const resultData = errorData.result || errorData;
	const isLauncherNotReady =
		errorMsg.includes("launcher_session_required") ||
		errorMsg.includes("booting") ||
		resultData?.launcherState === "booting" ||
		resultData?.code === "launcher_session_required";

	if (isLauncherNotReady) {
		sendToTerminal(
			"🔍 [TOOLS_ERROR] Launcher não pronto detectado",
			{
				message: errorMsg,
				launcherState: resultData?.launcherState,
				code: resultData?.code,
			},
			LogCategory.API,
		);

		return new AppError(
			"O sistema está inicializando. Aguarde alguns segundos e tente novamente.",
			{
				code: ErrorCode.LAUNCHER_NOT_READY,
				title: "Sistema Inicializando",
				severity: "warning",
				metadata: {
					status: error.status,
					launcherState: resultData?.launcherState,
					isTransient: true, // Indica que é um erro transitório
				},
			},
		);
	}

	// Extrair código específico da API (usar cópia)
	const apiCode = extractApiErrorCode(error);

	// Tentar mapear código específico primeiro
	if (apiCode && API_CODE_TO_ERROR_CODE[apiCode]) {
		const errorCode = API_CODE_TO_ERROR_CODE[apiCode];

		sendToTerminal(
			"🔍 [TOOLS_ERROR] Código específico mapeado",
			{
				apiCode,
				errorCode,
				context: detectedContext,
				status: error.status,
			},
			LogCategory.API,
		);

		// Buscar mensagem no ErrorCatalog
		const catalogEntry = ErrorCatalog[errorCode];

		if (catalogEntry) {
			const message =
				typeof catalogEntry.message === "function"
					? catalogEntry.message(errorData)
					: catalogEntry.message;

			return new AppError(message, {
				code: errorCode,
				title: catalogEntry.title,
				severity: catalogEntry.severity || "error",
				metadata: {
					status: error.status,
					apiCode,
					context: detectedContext,
					data: errorData,
				},
			});
		}
	}

	// Tentar mapear por status code + contexto
	const statusCodeErrorCode = mapStatusCodeToErrorCode(
		error.status,
		detectedContext || null,
		errorData.url || error.message,
	);

	if (statusCodeErrorCode) {
		sendToTerminal(
			"🔍 [TOOLS_ERROR] Status code mapeado",
			{
				status: error.status,
				errorCode: statusCodeErrorCode,
				context: detectedContext,
			},
			LogCategory.API,
		);

		// Buscar mensagem no ErrorCatalog
		const catalogEntry = ErrorCatalog[statusCodeErrorCode];

		if (catalogEntry) {
			const message =
				typeof catalogEntry.message === "function"
					? catalogEntry.message(errorData)
					: catalogEntry.message;

			return new AppError(message, {
				code: statusCodeErrorCode,
				title: catalogEntry.title,
				severity: catalogEntry.severity || "error",
				metadata: {
					status: error.status,
					context: detectedContext,
					data: errorData,
				},
			});
		}
	}

	// Fallback: usar fromApi padrão
	const baseError = fromApi(error, {
		metadata: {
			context: detectedContext,
			apiCode,
		},
	});

	sendToTerminal(
		"⚠️ [TOOLS_ERROR] Usando fallback genérico",
		{
			status: error.status,
			context: detectedContext,
			apiCode,
			errorCode: baseError.code,
		},
		LogCategory.API,
	);

	return baseError;
}

/**
 * Verifica se um erro é retryable (502, 504)
 */
export function isRetryableError(error: APIError | AppError): boolean {
	if (error instanceof APIError) {
		return error.status === 502 || error.status === 504;
	}

	if (error instanceof AppError) {
		return (
			error.code === ErrorCode.BAD_GATEWAY ||
			error.code === ErrorCode.GATEWAY_TIMEOUT
		);
	}

	return false;
}
